"use client";

import { Button } from "../../components/ui/button";
import { Clock, ArrowRight, Search, Tag } from "lucide-react";
import { motion } from "motion/react";
import { useAppSelector } from "../../_hooks/hooks";
import { calculateReadingTime } from "../../_helper/helper";
import Link from "next/link";
import { OptimizedImage } from "../../components/common/OptimizedImage";
import dynamic from "next/dynamic";
const Preloader = dynamic(() =>
  import("../../components/Preloader").then((mod) => mod.Preloader),
);

export default function BlogListing({
  initialBlogs,
}: {
  initialBlogs?: any[];
}) {
  const homeLoading = useAppSelector((state) => state.home.loading);
  const BannerList = useAppSelector((state) => state.home.BannerList);

  const BlogList = initialBlogs;

  const banner: any =
    BannerList?.find((banner) => banner?.BannerType === "Blogs") || [];

  const featuredBlog = BlogList?.[0];

  return (
    <>
      {homeLoading && <Preloader />}
      <div className="min-h-screen bg-white text-md">
        {/* Hero Section */}
        <section className="relative min-h-[100vh] flex items-end justify-center overflow-hidden">
          <div
            className="absolute inset-0 z-0"
            style={{
              backgroundImage: `url('${banner?.BannerImage || "/fallbacks/blog.jpg"}')`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/80" />
          </div>

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {banner?.SubTitle && (
                <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-md border border-white/20 px-8 py-4 rounded-full mb-8">
                  <Tag className="h-5 w-5 text-[#C9A962]" />
                  <span className="text-[#C9A962] tracking-[0.4em] text-sm">
                    {banner?.SubTitle || "INSIGHTS & STORIES"}
                  </span>
                </div>
              )}

              <h1 className="text-white text-3xl md:text-4xl lg:text-5xl mb-10 leading-[1.1] tracking-tight font-playfair">
                <span className="text-[#C9A962]">
                  {banner?.BannerTitle || "The Kavish Journal"}
                </span>
              </h1>

              {/* <div className="w-32 h-1 bg-[#C9A962] mx-auto mb-12" /> */}

              {banner?.Description && (
                <p
                  className="text-white/95 text-2xl md:text-3xl max-w-4xl mx-auto mb-16 leading-relaxed font-playfair"
                  dangerouslySetInnerHTML={{
                    __html: banner?.Description || "",
                  }}
                />
              )}
            </motion.div>
          </div>
        </section>

        {/* Featured Blog */}
        {featuredBlog && (
          <section className="py-20 bg-white">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                >
                  <div className="relative aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl group">
                    <OptimizedImage
                      src={featuredBlog?.BlogBannerImage}
                      alt={featuredBlog?.Title}
                      className="w-full h-full group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute top-6 left-6 bg-[#B42025] text-white px-4 py-2 rounded-full">
                      <span className="text-sm font-medium">FEATURED</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                >
                  <h2 className="text-xl md:text-4xl mb-8 text-[#1A1A1A] leading-tight">
                    {featuredBlog?.Title}
                  </h2>

                  <p
                    className="text-gray-700 leading-relaxed mb-8 line-clamp-3"
                    dangerouslySetInnerHTML={{
                      __html: featuredBlog?.ShortDescription,
                    }}
                  />

                  <div className="flex items-center gap-4 mb-10">
                    <div className="flex items-center gap-2 text-gray-500">
                      <Clock className="h-4 w-4" />
                      <span className="text-sm">
                        {calculateReadingTime(featuredBlog?.Description || "")}
                      </span>
                    </div>
                  </div>

                  <Link href={`/blogs/${featuredBlog?.BlogURL || ""}/`}>
                    <Button className="bg-[#B42025] hover:bg-[#8F1A1E] text-white px-10 py-6 text-lg group">
                      Read Full Article
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </motion.div>
              </div>
            </div>
          </section>
        )}

        {/* Blog Grid */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
              {BlogList?.slice(1)?.map((blog: any, index: number) => {
                const readingTime = calculateReadingTime(
                  blog?.Description || "",
                );

                return (
                  <motion.article
                    key={blog.ID}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="group cursor-pointer"
                  >
                    <div className="rounded-2xl overflow-hidden shadow-lg group-hover:shadow-2xl transition-shadow">
                      <Link href={`/blogs/${blog?.BlogURL || ""}/`}>
                        <div className="relative aspect-[4/3] overflow-hidden mb-1  ">
                          <OptimizedImage
                            src={blog?.BlogBannerImage}
                            alt={blog?.Title}
                            className="w-full h-full group-hover:scale-110 transition-transform duration-700"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        </div>
                      </Link>

                      <div className="space-y-4 px-4 py-4">
                        <Link href={`/blogs/${blog?.BlogURL || ""}/`}>
                          <h3 className="text-2xl text-[#1A1A1A] font-medium group-hover:text-[#B42025] transition-colors leading-tight mb-3">
                            {blog.Title}
                          </h3>
                        </Link>

                        <p
                          className="text-gray-600 line-clamp-2"
                          dangerouslySetInnerHTML={{
                            __html: blog.ShortDescription,
                          }}
                        />

                        <div className="flex items-center gap-3 pt-4">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-600">
                              {readingTime}
                            </span>
                          </div>
                        </div>
                        <Link
                          href={`/blogs/${blog?.BlogURL || ""}/`}
                          className="cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive hover:bg-primary/90 h-9 px-4 py-2 has-[>svg]:px-3 w-full bg-gradient-to-r from-[#b42025] to-[#a01830] hover:from-[#a01830] hover:to-[#b42025] text-white shadow-lg hover:shadow-xl transition-all duration-300 mt-auto"
                        >
                          Read Full Article →
                        </Link>
                      </div>
                    </div>
                  </motion.article>
                );
              })}
            </div>

            {/* No Results */}
            {(!BlogList || BlogList.length === 0) && (
              <div className="text-center py-20">
                <Search className="h-16 w-16 text-gray-300 mx-auto mb-6" />
                <h3 className="text-2xl text-gray-700 mb-4">
                  No articles found
                </h3>
                <p className="text-gray-500">
                  Try a different search or category
                </p>
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
}
